import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import "moment-timezone";
import { FC, useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import Flex from "../../../../../../components/Flex";
import StyledDialog from "../../../../../../components/StyledDialog";
import Text from "../../../../../../components/Text";
import ConfirmationModal from "../../../../../../components/app/ConfirmationModal";
import { useQueryParams } from "../../../../../../hooks/use-query-param";
import { useReduceDisclosure } from "../../../../../../hooks/use-reduce-disclosure";
import { formatDate } from "../../../../../../utils/date-time-utility";
import { getIdFromObj, getValueFromObj } from "../../../../../../utils/utils";
import SendBulkEmailModal from "../../../communications/email/SendBulkEmailModal";
import {
  ToolBarPropsType,
  ViewType,
} from "../../../scheduling/SchedulingLayout";
import { getCalcFilterDate } from "../../../scheduling/scheduling-constant";
import taskCalendarEvent from "./TaskCalendarEvent";
import ToolBar from "./TaskCalendarToolBar";
import BatchResolveDrawer from "../BatchResolveDrawer";
import useDisclosure from "../../../../../../hooks/use-disclosure";

interface TaskCalendarProps {
  tasks: any;
  filterDate: [string, string];
  setFilterDate: (filterDate: [string, string]) => void;
}
export const TaskCalendar: FC<TaskCalendarProps> = ({
  tasks,
  filterDate,
  setFilterDate,
}) => {
  //todo: as per B.A event should start by dueDate-15mn and end by dueDate
  const EVENT = tasks?.map((task: any) => {
    const startTime = new Date(task?.dueDate).getTime() - 15 * 60 * 1000;
    const endTime = new Date(task?.dueDate).getTime() - 1 * 60 * 1000;

    return {
      ...task,
      title: task.title,
      start: new Date(startTime),
      end: new Date(endTime),
    };
  });

  // sideEffect to set the filter date to the current month for first render
  useEffect(() => {
    setFilterDate(getCalcFilterDate(filterDate, "month", new Date(), "month"));
  }, []);

  const localizer = momentLocalizer(moment);
  const [view, setView] = useState<ViewType>("month");

  function toolBarComponent(props: ToolBarPropsType) {
    return (
      <ToolBar
        {...props}
        view={view}
        handleChangeView={(v) => {
          setFilterDate(getCalcFilterDate(filterDate, v, new Date(), view));
          setView(v);
        }}
      />
    );
  }

  const eventPropGetter = () => ({
    style: {
      backgroundColor: "#FCD27F",
      color: "white",
    },
  });

  const onActionClick = (row: any) => {
    setSelectedTaskForActivity(row);
    const actionDetail = getActionValue(row);

    if (Object.values(row?.actions)[0] === "Email") {
      sendMailDisclosure.open([actionDetail]);
    }
  };

  const isProviderTask =
    useQueryParams("activeTask") === "provider-centric-tasks";

  const [selectedTaskForActivity, setSelectedTaskForActivity] =
    useState<any>(null);

  const getActionValue = (row: any) => {
    const uuid = isProviderTask
      ? getIdFromObj(row?.assignTo)
      : getIdFromObj(row?.patient);
    const name = isProviderTask
      ? getValueFromObj(row?.assignTo)
      : getValueFromObj(row?.patient);
    return {
      uuid,
      name,
    };
  };

  const [sendMailDisclosure, confirmResolveDisc, showMoreDisclosure] =
    useReduceDisclosure(3);
  const resolveTaskDisclosureHost = useDisclosure(false, {
    onClose: () => {
      showMoreDisclosure.close();
    },
  });

  const handleConfirmResolve = () => {
    resolveTaskDisclosureHost.open([selectedTaskForActivity?.uuid]);
    confirmResolveDisc.close();
  };
  const queryClient = useQueryClient();

  const refetch = () => {
    queryClient.invalidateQueries({
      queryKey: ["collaboration-task-list"],
    });
  };

  //todo :unComment for edit
  // const onEdit = (row: any) => {
  //   taskDisclosureHost.open(getTaskDataEditPayload(row));
  // };

  return (
    <Box>
      {/* <UpsertTaskDrawer
        disclosureHost={taskDisclosureHost}
        patientData={taskDisclosureHost.data?.patient}
        taskType={"single_task"}
      /> */}

      <BatchResolveDrawer disclosureHost={resolveTaskDisclosureHost} />

      <TaskShowMoreDrawer
        disclosureHost={showMoreDisclosure}
        onTaskClick={onActionClick}
      />

      <SendBulkEmailModal
        disClosureHost={sendMailDisclosure}
        onSave={() => {
          confirmResolveDisc.open();
          refetch();
        }}
      />

      <ConfirmationModal
        maxWidth={"sm"}
        onConfirm={handleConfirmResolve}
        disclosureHost={confirmResolveDisc}
        confirmationText="Yes"
        cancelText="No"
        title={
          <Text fontWeight={"medium"} fontSize={"24px"}>
            Do you want resolve this task?
          </Text>
        }
      />

      <Box sx={classShowMore}>
        <Calendar
          key={view}
          localizer={localizer}
          // step={7.5}
          events={EVENT}
          startAccessor="start"
          endAccessor="end"
          date={filterDate[0]}
          view={view}
          onShowMore={(events) => {
            showMoreDisclosure.open(events);
          }}
          onNavigate={(date) => {
            setFilterDate(getCalcFilterDate(filterDate, view, date, view));
          }}
          eventPropGetter={() => {
            // let backgroundColor = "red";
            // APPOINTMENT_STATUS[event.status]?.color;
            return {
              style: {
                border: "1px solid #E5E5E5",
                // backgroundColor: backgroundColor,
                backgroundColor: "white",
                borderRadius: 6,
                color: "black",
                width: "100%",
                maxWidth: "97%",
                marginLeft: "1%",
                marginBottom: "2px",
                minHeight: "21px",
                ...eventPropGetter(),
              },
            };
          }}
          style={{
            height: "calc(100vh - 175px)",
            fontFamily: "roboto",
          }}
          components={{
            toolbar: toolBarComponent,
            event: (props) => taskCalendarEvent(props, view, onActionClick),
          }}
        />
      </Box>
    </Box>
  );
};

const TaskShowMoreDrawer = ({
  disclosureHost,
  onTaskClick,
}: {
  disclosureHost: any;
  onTaskClick: (task: any) => void;
}) => {
  const tasks = disclosureHost?.data || [];
  const selectedDate = tasks[0]?.createdDate || tasks[0]?.dueDate;

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      dialogTitle={`All Tasks for ${formatDate(selectedDate)}`}
      maxWidth="md"
    >
      <Flex flexDirection={"column"} gap={1} maxHeight="60vh" overflow="auto">
        {tasks.length === 0 ? (
          <Text color="text.secondary" textAlign="center" py={2}>
            No tasks found for this day
          </Text>
        ) : (
          tasks.map((task: any, index: number) => (
            <Box
              key={task.uuid || index}
              sx={{ cursor: "pointer" }}
              onClick={() => {
                disclosureHost.close();
                onTaskClick(task);
              }}
              width={"100%"}
            >
              {taskCalendarEvent({ event: task } as any, "month", onTaskClick)}
            </Box>
          ))
        )}
      </Flex>
    </StyledDialog>
  );
};

const classShowMore = {
  ".rbc-event": {
    height: "56px",
    width: "96% !important",
  },
};
